import { useContext } from 'react'
import { HomeContext } from '../../state/home-context'
import { useApiGetAnnualCommitmentMonitoring } from 'core/api/home/productivity/useApiGetAnnualCommitmentMonitoring'
import purse from 'core/assets/images/icons/purse.svg'
import { VpIcon } from '@vtmn-play/react'
import CircularProgressBar from './circular-progress-bar'
import WidgetLoading from '../../../../components/common/cards/widget-loading'
import { isHomeApiEnabled } from 'core/api/home/home-api-utils'

const isDefined = (value: number | null | undefined) => {
  return value != null && value !== 0
}

const teamSuffixMap: Record<string, string> = {
  COMMERCIAL: 'commercial-team',
  RECEPTION_AND_TILL: 'cashier-team',
  GENERAL_SERVICES: 'general-services-team'
}

const getTooltipTranslationKey = (metric: 'gmv' | 'hours', isInstore: boolean, isTeam: boolean, teamType?: string) => {
  const channel = isInstore ? 'instore' : 'omni'
  const suffix = isTeam && teamType && teamSuffixMap[teamType] ? teamSuffixMap[teamType] : 'store'

  return `general.homepage.annual-commitment-monitoring.${metric}.tooltip.${channel}.${suffix}`
}

const AnnualCommitmentMonitoring = () => {
  const { aggregation, teamId, channel, storeId, team } = useContext(HomeContext)

  const { data, isLoading: isLoadingAnnualCommitmentMonitoring } = useApiGetAnnualCommitmentMonitoring(
    { storeId, teamId, channel, aggregation },
    isHomeApiEnabled(storeId, teamId, aggregation)
  )

  const { realizedGmv = 0, forecastedGmv = 0, spentHours = 0, forecastedHours = 0 } = data || {}

  const gmvPercentage = forecastedGmv ? (realizedGmv / forecastedGmv) * 100 : 0
  const hoursPercentage = forecastedHours ? (spentHours / forecastedHours) * 100 : 0

  const isInstore = channel === 'INSTORE'
  const isTeam = aggregation === 'TEAM'

  const teamType = team?.team_type

  return (
    <>
      {isLoadingAnnualCommitmentMonitoring ? (
        <WidgetLoading
          skeletonCount={2}
          skeletonProps={{
            variant: 'graphic',
            shape: 'rounded',
            animation: 'pulse',
            style: {
              '--skeleton-width': '100%',
              '--skeleton-height': 'var(--vp-semantic-sizing-7xl)'
            } as React.CSSProperties
          }}
          className="mg-annual-commitment-monitoring-loading"
        />
      ) : (
        <div data-test-id="annual-commitment-monitoring" className="mg-annual-commitment-monitoring">
          <CircularProgressBar
            hasRealized={isDefined(data?.realizedGmv)}
            hasForecasted={isDefined(data?.forecastedGmv)}
            percentage={gmvPercentage}
            label="general.homepage.annual-commitment-monitoring.gmv.realized-gmv"
            errorMessage="general.homepage.annual-commitment-monitoring.gmv.error-message"
            tooltipMessage={getTooltipTranslationKey('gmv', isInstore, isTeam, teamType)}
            valueText={null}
            src={purse}
            className="blue-circular-progress-bar"
          />

          <CircularProgressBar
            hasRealized={isDefined(data?.spentHours)}
            hasForecasted={isDefined(data?.forecastedHours)}
            percentage={hoursPercentage}
            label="general.homepage.annual-commitment-monitoring.hours.spent-hours"
            errorMessage="general.homepage.annual-commitment-monitoring.hours.error-message"
            tooltipMessage={getTooltipTranslationKey('hours', isInstore, isTeam, teamType)}
            valueText={<VpIcon {...({ name: 'timer', size: 24 } as any)} />}
            className="purple-circular-progress-bar"
          />
        </div>
      )}
    </>
  )
}

export default AnnualCommitmentMonitoring
